import { env } from '../../env';
import apiManager from '../../api/api-manager';
import { PersonaTipoEnum } from '../../api/generated';
import {
  DocumentBeanComplexMetadata,
  DocumentNestedGroup,
} from '../../api/generated/papyrus-metadata';
import {
  BundleState,
  BundleStateBundleStatusEnum,
  RecipientStateRecipientStatusEnum,
} from '../../api/generated/signroom-orchestrator';
import {
  filterSignatureDocumentBundle,
  getSignatureDocumentType,
  parseBundles,
  parseBundlesToSignatureDocuments,
  parseModule,
  parseSignatureDocumentBundle,
  sortDocuments,
} from '../../api/parsers/signature-area-parsers';
import {
  SignatureDocument,
  SignatureDocumentModule,
  SignatureDocumentType,
} from '../../model';
import { DIY_ONBOARDING_CODE } from '../../model/signature-area/signature-document-type-lists';
import { UserRegistrationDocumentCodes } from '../../model/user-registration/user-registration-document-type';
import { SignatureDocumentError, UserRegistrationState } from '../../store';
import { AsyncThunkConfig } from '../../store/async-thunk-config';
import { MAX_ATTEMPTS, MAX_ATTEMPTS_ADDITION } from '../../utils';
import {
  getAPIRequestHeaders,
  getAPIRequestHeadersProspect,
  getAPIRequestHeadersWithToken,
  getBundlesDetails,
  getCleanBundleId,
  GetThunkAPI,
  searchBundles,
  searchDocuments,
  shouldSkipBundleApiCall,
} from '../../utils/api-utils';
import { DispositionMetadataBean } from '../../api/generated/papyrus-stream';
import { SignatureAreaRepository } from './signature-area-repository';

const getBundleStatus = async (
  bundleId: string,
  thunkApi: GetThunkAPI<AsyncThunkConfig<SignatureDocumentError>>
) => {
  if (shouldSkipBundleApiCall(bundleId)) {
    return null;
  }
  const cleanId = getCleanBundleId(bundleId);
  const state = thunkApi.getState();
  const { prospectCode } = state?.features?.home?.practiceData?.value ?? {};
  const result = await apiManager.signroomProviderApi.getBundleState1(
    cleanId,
    prospectCode
      ? getAPIRequestHeadersProspect(thunkApi)
      : getAPIRequestHeaders(thunkApi)
  );
  return result.data.result;
};

const enrichDocumentsWithRtoDetails = async (
  documents: SignatureDocument[],
  thunkApi: GetThunkAPI<AsyncThunkConfig<SignatureDocumentError>>
): Promise<SignatureDocument[]> => {
  return await Promise.all(
    documents.map(async (doc) => {
      const isRto = [doc.type, doc.type?.practiceType].includes('RtoImiOrder');
      if (isRto && doc.practiceNumber) {
        try {
          const res = await apiManager.signroomApi.getOrdineRto(
            doc.practiceNumber,
            getAPIRequestHeaders(thunkApi)
          );
          if (res.data.result) {
            const enrichedModules = doc.modules?.map((module: any) => {
              if (module.type === 'RtoImiModule') {
                return {
                  ...module,
                  moduleInformation: module.moduleInformation?.map(
                    (info: any) => ({
                      ...info,
                      tipoProdotto: res.data.result.tipoProdotto,
                      parametroTempo: res.data.result.parametroTempo,
                      parametroPrezzo: res.data.result.parametroPrezzo,
                      limitePrezzo: res.data.result.limitePrezzo?.toString(),
                      isin: res.data.result.isin,
                    })
                  ),
                  moduleChecks: {
                    ...module.moduleChecks,
                    appropriatenessOutcome: res.data.result.flagAppropriato
                      ? 'Appropriato'
                      : 'Non Appropriato',
                    complex: res.data.result.complessita,
                    ballIn: res.data.result.bailIn !== 'Non applicabile',
                    illiquid: res.data.result.illiquidity !== 'No',
                    conflictOperation: res.data.result.flagConflittoInteresse,
                  },
                };
              }
              return module;
            });
            return {
              ...doc,
              modules: enrichedModules,
            };
          }
        } catch (e) {
          console.error(
            `Error fetching enriched data for ${doc.practiceNumber}`,
            e
          );
        }
      }
      return doc;
    })
  );
};

const papyrusSignatureAreaRepository: SignatureAreaRepository = {
  downloadSignroomDocuments: async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<SignatureDocumentError>>,
    customerCode?: string,
    bundleCode?: string
  ): Promise<SignatureDocument[]> => {
    if (customerCode && bundleCode) {
      const token: string = thunkApi.getState().data.user.prospectToken;
      const result = await apiManager.documentale.getBundle(
        bundleCode,
        `Bearer ${token}`
      );

      const details = [
        {
          documents: result.data.result?.documents.map(
            (doc) =>
              ({
                bundleId: bundleCode,
                category: DIY_ONBOARDING_CODE,
                documentType: doc.documentCode,
                documentName: doc.documentName,
                complexMetadata: {
                  planId: '',
                },
              } as unknown as DocumentBeanComplexMetadata)
          ),
          details: result.data.result,
        },
      ];

      const documents = parseSignatureDocumentBundle(
        details,
        thunkApi.getState().data.user.userCode,
        thunkApi.getState().data.user.isProspect,
        parseBundles(details.map((d) => d.details)) || []
      );

      return await enrichDocumentsWithRtoDetails(documents, thunkApi);
    }

    const behalf =
      thunkApi.getState().data.user.userType === PersonaTipoEnum.Pg ||
      thunkApi.getState().features.userRegistration.selectedOptionUserType ===
        PersonaTipoEnum.Pg;

    const documentBundles = await searchBundles(thunkApi, undefined, behalf);

    const userCode = thunkApi.getState().data.user.userCode;

    const filteredDocumentBundles = documentBundles.filter((bundle) => {
      if (!bundle.bundleId || bundle.bundleId.includes('mig-feq')) {
        return false;
      }
      if (bundle.processTypeCode === 'DigitalServiceMembership') {
        return false;
      }
      const currentUserRecipient = bundle.recipients?.find(
        (r) => r.userId === userCode
      );
      if (!currentUserRecipient) {
        return false;
      }
      return behalf
        ? !!currentUserRecipient?.userBehalfOfId
        : !currentUserRecipient?.userBehalfOfId;
    });

    const isProspect = thunkApi.getState().data.user.isProspect;
    return parseBundlesToSignatureDocuments(
      filteredDocumentBundles,
      userCode,
      isProspect
    );
  },
  downloadSignatureDocumentForRegistration: async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<SignatureDocumentError>>
  ): Promise<SignatureDocument> => {
    const state: UserRegistrationState =
      thunkApi.getState().features.userRegistration;
    const customerCode = state.userCode;
    let result: { [key: string]: DocumentNestedGroup } = {};
    const searchParams = {
      documentTypes: UserRegistrationDocumentCodes as unknown as string[],
      expired: false,
      customerCode,
      bundleId: `${state.bundleId}@FEQ`,
    };

    const totalAttempts = MAX_ATTEMPTS + MAX_ATTEMPTS_ADDITION;

    for (let attempt = 0; attempt < totalAttempts; attempt++) {
      result = await searchDocuments(thunkApi, searchParams, state.tmpToken);

      if (Object.keys(result).length > 0) {
        break;
      }

      if (attempt < totalAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }
    }

    const bundles = filterSignatureDocumentBundle(result);

    const details = await getBundlesDetails(bundles, thunkApi, state.tmpToken);

    return parseSignatureDocumentBundle(details, customerCode, false)?.[0];
  },
  retrieveBundleUrlForSignature: async (
    bundleId: string,
    thunkApi: GetThunkAPI<AsyncThunkConfig<SignatureDocumentError>>,
    token?: string
  ): Promise<string> => {
    if (shouldSkipBundleApiCall(bundleId)) {
      return '';
    }
    const state = thunkApi.getState();
    const isProspect = state?.data?.user?.isProspect;
    if (isProspect) {
      const { prospectCode, bundleCode } =
        state?.features?.home?.practiceData?.value ?? {};
      const result = await apiManager.documentale.getBundleUrl(
        bundleCode,
        prospectCode,
        undefined,
        undefined,
        getAPIRequestHeadersWithToken(thunkApi, state.data.user?.prospectToken)
      );
      return result.data.result.url;
    } else {
      const userCode = token
        ? state.features.userRegistration.userCode
        : state.data.user.userCode;
      const isPG = token
        ? state.features.userRegistration.selectedOptionUserType ===
          PersonaTipoEnum.Pg
        : state.data.user.userType === PersonaTipoEnum.Pg;
      const userBehalfOfCode =
        isPG && !token
          ? state.data.user.userBehalfOfCode
          : state.features.userRegistration.userBehalfOfCode;
      const cleanId = getCleanBundleId(bundleId);
      const result = await apiManager.signroomProviderApi.getBundleUrl1(
        cleanId,
        userCode,
        userBehalfOfCode,
        isPG,
        getAPIRequestHeadersWithToken(thunkApi, token)
      );
      return result.data.result;
    }
  },
  rejectBundleAction: async (
    bundleId: string,
    thunkApi: GetThunkAPI<AsyncThunkConfig<SignatureDocumentError>>
  ): Promise<string> => {
    if (shouldSkipBundleApiCall(bundleId)) {
      return '';
    }
    const cleanId = getCleanBundleId(bundleId);
    const result = await apiManager.signroomProviderApi.rejectBundle1(
      {
        bundleCode: cleanId,
        customerCode: thunkApi.getState().data.user.userCode,
      },
      getAPIRequestHeaders(thunkApi)
    );
    return result.data.result;
  },
  retrieveBundleStatusAction: async (
    bundleId: string,
    thunkApi: GetThunkAPI<AsyncThunkConfig<SignatureDocumentError>>
  ): Promise<
    BundleStateBundleStatusEnum | RecipientStateRecipientStatusEnum
  > => {
    if (shouldSkipBundleApiCall(bundleId)) {
      return 'SIGNED' as BundleStateBundleStatusEnum;
    }

    let bundleObj: BundleState;
    let status: BundleStateBundleStatusEnum | RecipientStateRecipientStatusEnum;
    const totalAttempts = MAX_ATTEMPTS + MAX_ATTEMPTS_ADDITION;

    const state = thunkApi.getState();
    const userCode = state.data.user.userCode;

    for (let attempt = 0; attempt < totalAttempts; attempt++) {
      bundleObj = await getBundleStatus(bundleId, thunkApi);

      if (!bundleObj || bundleObj.bundleStatus === 'SIGNED') {
        // If bundle not found, consider as SIGNED to avoid further checks
        status = 'SIGNED' as BundleStateBundleStatusEnum;
        break;
      }

      // Apply status of the current user if present in the recipients list
      // this avoids to return the status SIGNING in case of a bundle with multiple recipients
      const actualRecipient = bundleObj.recipients.find(
        (f) => f.roleTypeCode === 'SIGNER' && f.userId === userCode
      );

      if (actualRecipient) {
        status = actualRecipient.recipientStatus;
      } else {
        status = bundleObj.bundleStatus;
      }

      if (status === 'SIGNED') {
        // Document is signed, no need to check again
        break;
      }

      if (attempt < totalAttempts) {
        // Wait for 1 second before the next attempt
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    return status;
  },
  registerDisposition: async (
    bundleId: string,
    thunkApi: GetThunkAPI<AsyncThunkConfig<SignatureDocumentError>>
  ): Promise<boolean> => {
    try {
      const response = await apiManager.regDispApi.registraDisposizione(
        { bundleId: bundleId },
        env.appModule,
        getAPIRequestHeaders(thunkApi)
      );
      return !!response;
    } catch (error) {
      console.error(error);
      return false;
    }
  },
  getBundleDocumentDetails: async (
    bundleId: string,
    thunkApi: GetThunkAPI<AsyncThunkConfig<SignatureDocumentError>>
  ): Promise<{
    bundleId: string;
    modules: SignatureDocumentModule[];
    practiceNumber: string;
    type?: SignatureDocumentType;
  }> => {
    const state = thunkApi.getState();
    const user = state.data.user;
    const isPG = user.userType === PersonaTipoEnum.Pg;
    const customerCode = user.userCode;
    const customerUserBehalfOfCode = isPG ? user.userBehalfOfCode : undefined;
    const MAX_PAGES = 30;
    let currentPage = 0;
    let total = 0;
    const rawDocs: DocumentBeanComplexMetadata[] = [];
    const docIds = new Set<string>();

    do {
      const response =
        await apiManager.azDocumentProviderApi.searchDocumentsGroupedByBundle1(
          isPG,
          {
            customerCode,
            customerUserBehalfOfCode,
            bundleId: `${bundleId}@FEQ`,
            signroom: true,
            scopes: ['CONTRACT', 'DISPOSITION'],
            paginator: {
              current: currentPage,
            },
          },
          getAPIRequestHeaders(thunkApi)
        );

      const paginator = response.data?.paginator;
      total = paginator?.total ?? 0;
      const totalPageNumber = paginator?.totalPageNumber;

      const resultMap = response.data?.result || {};
      let docsAddedThisPage = 0;

      for (const [, value] of Object.entries(resultMap)) {
        let docsList: any[] = [];
        if (Array.isArray(value)) {
          // Jackson polymorphism array: ["com.objectway.papyrus.response.DocumentLastGroupNode", [ doc1, doc2, ... ]]
          if (Array.isArray(value[1])) {
            docsList = value[1];
          } else if (
            value.every(
              (item) =>
                typeof item === 'object' &&
                item !== null &&
                !Array.isArray(item)
            )
          ) {
            docsList = value;
          } else {
            for (const item of value) {
              if (Array.isArray(item)) {
                docsList.push(...item);
              }
            }
          }
        } else if (value && typeof value === 'object') {
          for (const item of Object.values(value as any)) {
            if (Array.isArray(item)) {
              docsList.push(...item);
            }
          }
        }

        for (const doc of docsList) {
          const docKey = doc?.id || doc?.dmsId || doc?.documentName;
          if (
            doc &&
            typeof doc === 'object' &&
            !Array.isArray(doc) &&
            docKey &&
            !docIds.has(docKey)
          ) {
            docIds.add(docKey);
            rawDocs.push(doc as DocumentBeanComplexMetadata);
            docsAddedThisPage++;
          }
        }
      }

      currentPage++;

      if (docsAddedThisPage === 0) {
        break;
      }

      const hasMorePages =
        totalPageNumber !== undefined
          ? currentPage < totalPageNumber
          : total > rawDocs.length;

      if (!hasMorePages) {
        break;
      }
    } while (currentPage < MAX_PAGES);

    if (rawDocs.length === 0) {
      return {
        bundleId,
        modules: [],
        practiceNumber: '',
      };
    }

    const sortedDocuments = [...rawDocs].sort(sortDocuments);
    const firstDoc = sortedDocuments[0];
    const complexMetadata = firstDoc.complexMetadata as DispositionMetadataBean;
    const practiceNumber = complexMetadata?.planId || '';

    const currentDoc = state.features.signatureArea.documents.items[bundleId];
    const rawBundle = currentDoc?.rawBundle;

    const isProspect = state.data.user.isProspect;
    const modules = sortedDocuments.map((item) =>
      parseModule(item, rawBundle as any, sortedDocuments, isProspect)
    );

    const type = getSignatureDocumentType(firstDoc, isProspect);

    return {
      bundleId,
      modules,
      practiceNumber,
      type,
    };
  },
  getOrdineRtoDetails: async (
    practiceNumber: string,
    thunkApi: GetThunkAPI<AsyncThunkConfig<SignatureDocumentError>>
  ): Promise<any> => {
    const res = await apiManager.signroomApi.getOrdineRto(
      practiceNumber,
      getAPIRequestHeaders(thunkApi)
    );
    return res.data?.result;
  },
};

export default papyrusSignatureAreaRepository;
